// WorkflowBuilder type definitions

import { Node } from '@xyflow/react';
import { MouseEvent as ReactMouseEvent } from 'react';
import { QualificationsResponse } from 'services/api/AgentCenterAPI';

// ============================================================================
// Operating Hours Types
// ============================================================================

export interface OperatingHoursSettings {
    startTime: string;
    endTime: string;
    timezone: string;
}

export interface WorkflowConfig {
    operatingHours?: OperatingHoursSettings;
}

// ============================================================================
// Node Data Types
// ============================================================================

export interface BaseNodeData extends Record<string, unknown> {
    label: string;
    subLabel?: string;
    icon?: string;
    onDelete?: (nodeId: string, event: ReactMouseEvent) => void | boolean;
}

export enum WorkflowTagsType {
    PREDEFINED = 'PREDEFINED',
    GENERIC = 'GENERIC',
}

export interface CallNodeData extends BaseNodeData {
    promptId: string;
    promptName: string;
    aiPrompt?: string; // For backward compatibility
    callText: string;
    tagsType: WorkflowTagsType;
}

export interface DelayNodeData extends BaseNodeData {
    delayType: 'TIMESTAMP' | 'DURATION';
    specificDateTime: string; // ISO string
    timeValue: string;
    timeUnit: 'minutes' | 'hours' | 'days';
}

export type SMSMode = 'static' | 'agentic';

export type TimeoutUnit = 'hours' | 'minutes';

export interface SMSNodeData extends BaseNodeData {
    smsMode: SMSMode;
    messageContent: string;
    langfusePromptId: string;
    userAbandonedTimeoutHours?: number;
    agentAbandonedTimeoutHours?: number;
    userAbandonedTimeoutMinutes?: number;
    agentAbandonedTimeoutMinutes?: number;
    userAbandonedTimeoutUnit?: TimeoutUnit;
    agentAbandonedTimeoutUnit?: TimeoutUnit;
    expectResponse: boolean;
}

export interface EmailNodeData extends BaseNodeData {
    messageContent: string;
}

export interface LeadNodeData extends BaseNodeData {
    crmStatus: string;
}

export type DecisionFieldType = 'call_disposition' | 'call_status' | 'party_stage' | 'sms_status';

/** One row in the DECISION node API `config.conditions` array. */
export interface DecisionCondition {
    decisionField: DecisionFieldType;
    values: string[];
    nextNode: string;
}

export interface DecisionNodeData extends BaseNodeData {
    conditions: DecisionCondition[];
    defaultNextNode?: string | null;
}

/** Editable row in the decision config modal (before nextNode ids exist). */
export type DecisionConditionFormRow = {
    decisionField: DecisionFieldType;
    values: string[];
    nextNodeType: 'CALL' | 'SMS' | 'DELAY' | 'END';
};

/** Terminal node – no config, no modal. Used as mapping target (e.g. UNQUALIFIED → End). */
export type EndNodeData = BaseNodeData;

export type NodeData =
    | BaseNodeData
    | CallNodeData
    | DelayNodeData
    | SMSNodeData
    | EmailNodeData
    | LeadNodeData
    | DecisionNodeData
    | EndNodeData;

// ============================================================================
// Node Types
// ============================================================================

export type NodeType =
    | 'defaultNode'
    | 'callNode'
    | 'delayNode'
    | 'smsNode'
    | 'emailNode'
    | 'leadNode'
    | 'decisionNode'
    | 'endNode'
    /** Main-canvas-only synthetic anchor; not persisted */
    | 'terminalBridgeNode';

export type ServerNodeType = 'CALL' | 'DELAY' | 'SMS' | 'EMAIL' | 'LEAD' | 'DECISION' | 'END' | 'UNKNOWN';

// ============================================================================
// Server Workflow Types
// ============================================================================

export interface ServerNodeConfig {
    delay_type?: 'TIMESTAMP' | 'DURATION';
    delay?: {
        timestamp?: string;
        duration?: number;
        unit?: 'MINUTES' | 'HOURS' | 'DAYS';
    };
    promptId?: string;
    promptName?: string;
    aiPrompt?: string;
    callText?: string;
    smsText?: string;
    messageContent?: string;
    smsMode?: 'static' | 'agentic';
    langfusePromptId?: string;
    userAbandonedTimeoutHours?: number;
    agentAbandonedTimeoutHours?: number;
    userAbandonedTimeoutMinutes?: number;
    agentAbandonedTimeoutMinutes?: number;
    userAbandonedTimeoutUnit?: TimeoutUnit;
    agentAbandonedTimeoutUnit?: TimeoutUnit;
    expectResponse?: boolean;
    crmStatus?: string;
    nextNode?: string | null;
    /** @deprecated Legacy DECISION shape; prefer `conditions`. */
    decisionField?: DecisionFieldType;
    /** @deprecated Legacy DECISION shape; prefer `conditions`. */
    mappings?: Record<string, { nextNode: string }>;
    conditions?: DecisionCondition[];
    defaultNextNode?: string | null;
}

export interface ServerNode {
    id: string;
    type: ServerNodeType;
    config: ServerNodeConfig;
    uiConfig: {
        dragging?: boolean;
        height?: number;
        position: { x: number; y: number };
        positionAbsolute?: { x: number; y: number };
        selected?: boolean;
        width?: number;
        label?: string;
    };
}

export interface WorkflowDefinition {
    nodes: ServerNode[];
    generic_tags?: Record<string, { type: string; prompt: string; order?: number }>;
    generic_prompts?: Record<string, string>;
    [key: string]: unknown; // Allow additional properties
}

export interface WorkflowData {
    id?: string;
    title: string;
    description: string;
    start_date: string | null;
    end_date: string | null;
    country_code?: string;
    language?: string;
    workflow_definition?: WorkflowDefinition;
    head_node_id?: string;
    /** Terminal transition DECISION node id (canonical). */
    terminal_decision_node_id?: string | null;
    deleted_node_ids?: string[] | null;
    // Request: WorkflowDefinition (nodes). Response: WorkflowConfig (operatingHours).
    // The backend uses different field semantics for the same JSON key in each direction.
    config?: WorkflowDefinition | WorkflowConfig;
    tag_type: WorkflowTagsType;
    human_transfer: boolean;
    operatingHours?: OperatingHoursSettings;
}

// ============================================================================
// Prompt Types
// ============================================================================

export interface Prompt {
    id: string;
    prompt_name: string;
}

export interface PromptsResponse {
    prompts: Prompt[];
}

// ============================================================================
// Component Props Types
// ============================================================================

export interface NodeComponentProps {
    id: string;
    data: NodeData;
}

export interface NodeConfigModalProps {
    node: Node<NodeData>;
    nodes?: Node<NodeData>[];
    /** When set, identifies the terminal routing decision (for dual-canvas terminal flow). */
    terminalDecisionNodeId?: string | null;
    onClose: () => void;
    onSave: (id: string, updates: Partial<NodeData>) => void;
    onDecisionDone?: (
        nodeId: string,
        rows: DecisionConditionFormRow[],
        defaultNextNodeType: 'CALL' | 'SMS' | 'DELAY' | 'END' | null
    ) => void;
}

export interface ActionItem {
    label: string;
    type: NodeType;
}

// ============================================================================
// Connection Types
// ============================================================================

export interface ConnectionStartParams {
    nodeId: string | null;
    handleId: string | null;
    handleType: 'source' | 'target' | null;
}

// ============================================================================
// Notification Types
// ============================================================================

export interface NotificationState {
    isOpen: boolean;
    message: string;
    title: string;
    type: 'success' | 'error' | 'warning' | 'info';
    callback: (() => () => void) | null;
}

// ============================================================================
// Validation Types
// ============================================================================

export interface ValidationResult {
    valid: boolean;
    issues: string[];
}

export interface TestGenericQualificationConfig {
    generic_tags: Record<string, { type: string; prompt: string; order?: number }>;
    generic_prompts: Record<string, string>;
}

export interface TestGenericQualificationRequest {
    transcript: string;
    config: TestGenericQualificationConfig;
}

export interface TestGenericQualificationResponse {
    qualification: {
        qualified: boolean;
        undetermined: boolean;
        unqualified: boolean;
    };
    extraction: { tags: QualificationsResponse[] };
}
